import { createColumnHelper, useTable } from '@tanstack/react-table';
import { UseArticlesQueryParams } from 'collection/pages/articles/Articles';
import { getArticlesViewActionColumn } from 'collection/pages/articles/components/articlesTable/columns';
import { useArticlesFilters } from 'collection/pages/articles/hooks/useArticlesFilters';
import {
  ArticleListItemResultFragment,
  ArticlesQuery,
  useArticlesQuery,
} from 'lib-knowledge-management-common/graphql';
import { isValidSearchLength } from 'lib-knowledge-utils/string';
import { useMemo, useRef } from 'react';

import { ArticlesTable } from 'components/articlesTable';
import {
  getArticleTitleColumn,
  getCollectionColumn,
  getPublicationStatusColumn,
} from 'components/articlesTable/columns';
import {
  articlesTableFeatures,
  ArticlesTableFeatures,
} from 'components/articlesTable/features';
import { Article } from 'components/articlesTable/types';

export function sortResultsByUpdatedAt(
  articlesData?: ArticlesQuery,
): ArticleListItemResultFragment[] {
  if (!articlesData) return [];

  return [...articlesData.articles.query.results].sort((a, b) =>
    a.updatedAt > b.updatedAt ? -1 : 1,
  );
}

export function ArticlesViewTable(): JSX.Element {
  const {
    articlesQueryVariables,
    store: { search },
  } = useArticlesFilters();

  const articlesQueryParams: UseArticlesQueryParams = {
    variables: articlesQueryVariables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  };
  const {
    data: articlesData,
    networkStatus,
    error,
    fetchMore,
    loading,
  } = useArticlesQuery(articlesQueryParams);

  if (error)
    throw new Error(`ArticlesViewTable > ${error.message}`, { cause: error });

  const articles: ArticleListItemResultFragment[] = useMemo(() => {
    return conditionalArticlesSort({ search, articlesData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articlesData, search, articlesQueryVariables]); // we want this to run if articlesQueryVariables updates

  const columnHelper = createColumnHelper<ArticlesTableFeatures, Article>();

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('title', getArticleTitleColumn()),
        columnHelper.accessor('parentCollections', getCollectionColumn()),
        columnHelper.accessor(
          'publicationStatus',
          getPublicationStatusColumn(),
        ),
        columnHelper.accessor('id', getArticlesViewActionColumn()),
      ]),
    [columnHelper],
  );
  const table = useTable({
    features: articlesTableFeatures,
    columns,
    data: articles,
  });
  const lastRowRef = useRef<HTMLTableRowElement>(null);

  const articlesQuery = articlesData?.articles.query;
  return (
    <ArticlesTable
      lastRowRef={lastRowRef}
      articlesQuery={articlesQuery}
      fetchMore={fetchMore}
      networkStatus={networkStatus}
      loading={loading}
    >
      <ArticlesTable.Header table={table} />
      <ArticlesTable.Body
        table={table}
        lastRowRef={lastRowRef}
        networkStatus={networkStatus}
      />
    </ArticlesTable>
  );
}
/** Sort articles depending on whether a search term is present */
export function conditionalArticlesSort({
  search,
  articlesData,
}: {
  search: string | null | undefined;
  articlesData: ArticlesQuery | undefined;
}): ArticleListItemResultFragment[] {
  if (search && isValidSearchLength(search)) {
    return articlesData?.articles.query.results || []; // Don't want to sort if the results have originated from a search term
  }
  return sortResultsByUpdatedAt(articlesData);
}
